/**
 * @file PageStructure.tsx
 * @description Структура открытой страницы в конструкторе (phase19.16
 * Р1-6а; Р2 — uiux-design-r2 §5, §6, §8, §9): подсказка новичку, «Шапка —
 * все стр.», блоки страницы (`BlockList`: перетаскивание, показ, «⋯»,
 * «+ блок сюда»), «Подвал — все стр.», [+ Добавить блок] (библиотека
 * заготовок) и «Блоков N из 30» (янтарный с 28, SB64). Пустая страница —
 * `EmptyPageStart`. Экран «Блог» — места над/под лентой и под статьёй;
 * страница места открывается со ссылкой назад к экрану «Блог».
 * Р2а: экран «Каталог» — места над/под списком (`CatalogScreenStructure`),
 * экран «Страница товара» — шаблон из блоков (`ProductTemplateStructure`);
 * страница места каталога — со ссылкой назад к экрану «Каталог».
 * SB120 этап 4: экран «Корзина» — строка «Корзина — вид в панели «Вид
 * корзины»» и пояснение «Состав корзины задаёт платформа…» (uiux-cart §3).
 * Р3а (uiux-r3 §3.1): у блоков метки черновика «● изменён / ● новый», под
 * блоками — «Удалено блоков: N [Что изменено ›]» (открывает «Публикации»).
 */

import { SITE_LIMITS, SITE_SLOTS } from "@chaiberi/shared";
import { ArrowLeft } from "lucide-react";

import { Button } from "../../ui/button";
import { useBuilder } from "../builder-context";
import { AddBlockMenu } from "./AddBlockMenu";
import { blocksFullText } from "../../../lib/site-builder/block-limits";
import { blockMarksOf } from "../../../lib/site-builder/page-diff-lines";
import { AreaRow } from "./AreaRow";
import { BlockList } from "./BlockList";
import { BlogScreenStructure } from "./BlogScreenStructure";
import { CatalogScreenStructure } from "./CatalogScreenStructure";
import { ProductTemplateStructure } from "./ProductTemplateStructure";
import { EmptyPageStart } from "./EmptyPageStart";
import { LockedScreenRow } from "./LockedScreenRow";
import { VersionStructure } from "./VersionStructure";
import { FirstRunGuide } from "./FirstRunGuide";
import { PAGES_FORBIDDEN_TEXT } from "../../../hooks/site-builder/useBuilderPages";
import type { BuilderPanelKey, BuilderScreenKey } from "../../../lib/site-builder/selection";
import { cn } from "@/lib/utils";

/** Пояснение экрана магазина без блоков (реестр: свой текст — запись; uiux-cart §3 — корзина). */
const SCREEN_STRUCTURE_DEFAULT = "Список товаров и фильтры задаёт платформа — на этом экране меняется только вид.";
const SCREEN_STRUCTURE_TEXT: Readonly<Partial<Record<BuilderScreenKey, string>>> = {
  cart: "Состав корзины задаёт платформа — здесь меняется только вид.",
};

/** Строка-замок «вид в панели …» у экрана со своим видом (SB120 этап 4: корзина). */
const SCREEN_LOOK_ROWS: Readonly<Partial<Record<BuilderScreenKey, { label: string; panel: BuilderPanelKey }>>> = {
  cart: { label: "Корзина — вид в панели «Вид корзины»", panel: "cart" },
};

/** С какого числа блоков счётчик становится янтарным (uiux-design-r2 §10). */
const BLOCKS_WARN_AT = SITE_LIMITS.sectionsPerPage - 2;

export function PageStructure() {
  const b = useBuilder();
  const { pages, selection } = b;
  const page = pages.page;
  const selectedArea = selection.sel.kind === "area" && !selection.panel ? selection.sel.area : null;
  const selectedBlock = selection.sel.kind === "block" && !selection.panel ? selection.sel.id : null;

  const header = <AreaRow area="header" selected={selectedArea === "header"} />;
  const footer = <AreaRow area="footer" selected={selectedArea === "footer"} />;

  if (selection.screen === "blog") return <BlogScreenStructure />;
  if (selection.screen === "catalog") return <CatalogScreenStructure />;
  if (selection.screen === "product" && b.canEditPages) return <ProductTemplateStructure />;
  if (selection.screen) {
    const look = SCREEN_LOOK_ROWS[selection.screen];
    return (
      <div className="flex flex-col gap-1">
        {header}
        {look ? <LockedScreenRow label={look.label} onOpen={() => void b.select({ panel: look.panel, sub: null })} /> : null}
        <p className="type-caption px-3 py-2 text-on-surface-variant">{SCREEN_STRUCTURE_TEXT[selection.screen] ?? SCREEN_STRUCTURE_DEFAULT}</p>
        {footer}
      </div>
    );
  }
  if (!b.canEditPages) {
    return (
      <div className="flex flex-col gap-1">
        {header}
        <p className="type-caption px-3 py-2 text-on-surface-variant">{PAGES_FORBIDDEN_TEXT}</p>
        {footer}
      </div>
    );
  }
  if (!page) return <p className="type-caption px-3 text-on-surface-variant">{pages.pageError ?? "Загружаем страницу…"}</p>;
  // Р3а (QA D15): на канве версия — слева её блоки, только чтение, без меток черновика.
  const viewing = b.override?.pageVersion;
  if (viewing && viewing.pageId === page.id) return <VersionStructure pageId={page.id} version={viewing.version} />;

  const count = page.blocks.length;
  const fullText = blocksFullText(page);
  const full = fullText !== null;
  const slot = page.kind === "SLOT" ? SITE_SLOTS.find((s) => s.key === page.slot) : null;
  const draft = blockMarksOf(b.publishing.changes?.pageId === page.id ? b.publishing.changes.diff : null);
  return (
    <div className="flex flex-col gap-1">
      {slot ? (
        <div className="flex flex-col gap-1 pb-2">
          <Button variant="ghost" className="self-start" onClick={() => void b.select({ pageId: null, screen: slot.screen, sel: { kind: "page" }, panel: null })}>
            <ArrowLeft aria-hidden />
            {slot.screen === "catalog" ? "Экран «Каталог»" : "Экран «Блог»"}
          </Button>
          <p className="type-caption px-1 text-on-surface-variant">
            {slot.label}: {slot.place}. Правки — в черновике, на сайте после «Опубликовать».
          </p>
        </div>
      ) : (
        <FirstRunGuide page={page} />
      )}
      {count > 0 ? (
        <div className="pb-1">
          <AddBlockMenu disabled={full} />
        </div>
      ) : null}
      {header}
      {count === 0 ? <EmptyPageStart /> : <BlockList blocks={page.blocks} marks={draft.marks} selectedId={selectedBlock} editable onSelect={(block) => void b.select({ sel: { kind: "block", id: block.id }, panel: null })} />}
      {draft.removed > 0 ? (
        <Button variant="ghost" className="justify-start text-on-surface-variant" onClick={b.openPublications}>
          Удалено блоков: {draft.removed} · Что изменено ›
        </Button>
      ) : null}
      {footer}
      <div className="flex flex-col gap-1 pt-2">
        {count > 0 ? <AddBlockMenu disabled={full} /> : null}
        <span className={cn("type-caption px-1", count >= BLOCKS_WARN_AT ? "text-warning" : "text-on-surface-variant")}>
          Блоков {count} из {SITE_LIMITS.sectionsPerPage}
        </span>
        {fullText ? <span className="type-caption px-1 text-on-surface-variant">{fullText}</span> : null}
      </div>
    </div>
  );
}
